/**
 * Reine Hilfen der Seite „Kältenachweis“ (TH-3, 17.10.2026).
 *
 * Fachstatus, Bereichstitel und Nachkommastellen sind DIESELBEN wie im
 * Bericht KAE-KMN (`Infrastructure/Kaelte/Kaeltenachweis.cs`,
 * `Kaeltenachweistexte`): eine Zahl, eine Darstellung (TX-STANDARD S0.5,
 * S1.5, S1.7). Ein Test hält beide Listen gleich.
 */

import type { KaeltenachweisErgebnis, KaeltenachweisLauf } from '../../api';
import type { Zustand } from '../../muster';
import { zeitpunkt } from './kaelte';

/** Die Bereiche in der Reihenfolge von Seite, Bericht und Liste (Kaeltelauf.Bereiche). */
export const BEREICHE: readonly string[] = [
  'erzeugung', 'teillast', 'redundanz', 'rueckkuehlung', 'eisspeicher', 'freikuehlung', 'en378', 'en378_matrix',
];

/** Der Bereich als Abschnittstitel (Kaeltenachweistexte.Bereich). */
export const BEREICH_TITEL: Record<string, string> = {
  erzeugung: 'Kälteerzeugung',
  teillast: 'Teillast und Jahresarbeit',
  redundanz: 'Redundanz n−1',
  rueckkuehlung: 'Rückkühlung',
  eisspeicher: 'Eisspeicher',
  freikuehlung: 'Freie Kühlung',
  en378: 'DIN EN 378 – Kältemittel und Aufstellung',
  en378_matrix: 'DIN EN 378 – Füllmengengrenzen je Zugangskategorie und Aufstellungsklasse',
};

/** Was der Bereich zeigt – ein Satz unter dem Abschnittstitel. */
export const BEREICH_BESCHREIBUNG: Record<string, string> = {
  erzeugung: 'Datenblatt in sich (Energiebilanz, EER) und installierte Leistung der Lesart gegen die Kühllastspitze.',
  teillast: 'Jahresgang des Kühllast-Laufs in Teillastklassen, ESEER aus Teillast-EER, Jahresstrom als Schätzung.',
  redundanz: 'Ausfall der größten Maschine bzw. eines Rückkühlers – ob n−1 gefordert ist, ist eine Vorgabe (HZK-32, HZK-11).',
  rueckkuehlung: 'Rückkühlleistung gegen die Maschinen und gegen die Abwärme bei Kühllastspitze, Auslegungsluft.',
  eisspeicher: 'Entzugsdauer, Ladezeit und der Auslegungstag Stunde für Stunde mit allen Maschinen.',
  freikuehlung: 'ε-NTU aus dem Auslegungspunkt der Rückkühler (wie Migration 0776), Stunden aus dem Jahresgang – obere Schätzung.',
  en378: 'Kältemittel R1234ze (A2L), Füllmengengrenze, Maschinenraum, Gaswarnung, Notlüftung, Mindestvolumen, EN 378-2, F-Gase.',
  en378_matrix: 'Füllmengengrenze je Zugangskategorie und Aufstellungsklasse aus den ungeprüften Normwerten (Migration 1251).',
};

/** Fachstatus nach TX-STANDARD S1.5; „vorläufig“ = eingehalten, aber auf unbestätigten Eingaben (HZK-31). */
export function zustandErgebnis(ergebnis: string): { zustand: Zustand; text: string } {
  switch (ergebnis) {
    case 'erfuellt': return { zustand: 'gut', text: 'erfüllt' };
    case 'nicht_erfuellt': return { zustand: 'fehler', text: 'nicht erfüllt' };
    case 'nicht_pruefbar': return { zustand: 'warnung', text: 'nicht prüfbar' };
    case 'vorlaeufig': return { zustand: 'warnung', text: 'vorläufig' };
    case 'hinweis': return { zustand: 'info', text: 'Hinweis' };
    case 'entfaellt': return { zustand: 'neutral', text: 'entfällt' };
    default: return { zustand: 'neutral', text: ergebnis };
  }
}

/** Nachkommastellen je Einheit (Kaeltenachweistexte.Nachkomma, Einheitenkatalog S1.7). */
export function nachkomma(einheit: string | null | undefined): number {
  switch (einheit) {
    case 'kW': case 'kWh': case 'MWh/a': case 'm³': case 'kg': case '°C': case '%': return 1;
    case 'm³/h': case 'h/a': case 'Stk': return 0;
    case 'h': case 't CO₂e': return 2;
    case 'kg/m³': case '-': return 3;
    default: return 1;
  }
}

/** Die Herkunft eines Werts im Klartext (S1.5); Vorgabe/Annahme/Normwert sind Warnung. */
export function zustandHerkunftNachweis(herkunft: string): { zustand: Zustand; text: string } {
  switch (herkunft) {
    case 'datenblatt': return { zustand: 'neutral', text: 'Datenblatt' };
    case 'modell': return { zustand: 'neutral', text: 'Modell' };
    case 'quelle': return { zustand: 'neutral', text: 'Quelle' };
    case 'lauf': return { zustand: 'neutral', text: 'Lauf' };
    case 'hand': case 'fachplaner': return { zustand: 'neutral', text: 'von Hand' };
    case 'norm': return { zustand: 'warnung', text: 'Normwert' };
    case 'annahme': return { zustand: 'warnung', text: 'Annahme' };
    case 'berechnet': return { zustand: 'warnung', text: 'gerechnet' };
    case 'vorgabe': return { zustand: 'warnung', text: 'Vorgabe' };
    default: return { zustand: 'warnung', text: herkunft };
  }
}

/** Der Rechenstand (S1.2): „Lauf ‹Kurz-ID› · gerechnet ‹…› · Kühllast-Lauf ‹Nr› vom ‹…›“. */
export function rechenstandNachweis(lauf: KaeltenachweisLauf | null): string {
  if (!lauf) return 'noch nicht gerechnet';
  const teile = [`Lauf ${lauf.id.slice(0, 8)}`, `gerechnet ${zeitpunkt(lauf.beendetAm ?? lauf.gestartetAm)}`];
  if (lauf.kuehllastLaufnummer !== null) {
    teile.push(`Kühllast-Lauf ${lauf.kuehllastLaufnummer} vom ${zeitpunkt(lauf.kuehllastAm)}`);
  }
  return teile.join(' · ');
}

/** Eine Zeile nach Regel. */
export function regel(zeilen: readonly KaeltenachweisErgebnis[], name: string): KaeltenachweisErgebnis | undefined {
  return zeilen.find((z) => z.regel === name);
}

/** Die Füllmengen-Matrix: Kategorie a/b/c × Klasse I–IV aus den Zeilen `en378_matrix_<kat>_<klasse>`. */
export interface MatrixZeile {
  kategorie: string;
  zellen: Record<string, KaeltenachweisErgebnis | undefined>;
}

export const KLASSEN = ['I', 'II', 'III', 'IV'] as const;

export function matrix(zeilen: readonly KaeltenachweisErgebnis[]): MatrixZeile[] {
  return ['a', 'b', 'c'].map((kategorie) => ({
    kategorie,
    zellen: Object.fromEntries(KLASSEN.map((k) => [
      k, zeilen.find((z) => z.regel === `en378_matrix_${kategorie}_${k.toLowerCase()}`),
    ])),
  }));
}

/** Der Zellentext der Matrix: Grenze in kg, „keine Begrenzung“ oder „nicht prüfbar“ (Regel oder Volumen fehlt). */
export function matrixText(z: KaeltenachweisErgebnis | undefined, format: (x: number, n: number) => string): string {
  if (!z) return '—';
  if (z.ergebnis === 'nicht_pruefbar' && z.grenzwert === null) return 'nicht prüfbar';
  if (z.grenzwert === null) return 'keine Begrenzung';
  return `≤ ${format(z.grenzwert, 1)} kg`;
}
